import React, { FC, useEffect, useMemo, useState } from 'react';
import {
    Input,
    InputGroup,
    InputLeftAddon,
    InputLeftElement,
    InputProps,
    InputRightAddon,
    InputRightElement
} from '@chakra-ui/react';

type CurrencyInputProps = InputProps & {
    /**
     * Whether to use the internal currency symbol.
     * This is currently the default behavior.
     * We eventually want to phase out the use of this prop and only use the internal symbol.
     */
    useInternalSymbol?: boolean;
    /** The currency code to use for the currency symbol. */
    currencyCode: string;
    /**
     * The locale to use for the currency symbol. Defaults to the browser locale.
     * Other locales should only be specified for testing purposes.
     */
    locale?: string | string[];
};

const CurrencyInput: FC<CurrencyInputProps> = ({
    useInternalSymbol = true,
    currencyCode = 'USD',
    locale = [],
    ...props
}) => {
    const { value, onChange, onBlur, height } = props;

    const [currentValue, setCurrentValue] = useState(Number(value).toFixed(2));

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setCurrentValue((previousValue) => Number(previousValue).toFixed(2));
        onBlur?.(event);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.persist(); // generally unnecessary to do this. we shouldn't be passing the event in a callback
        setCurrentValue(event.target.value);
        onChange?.(event);
    };

    useEffect(() => {
        // This hook is needed for syncing the local state `currentValue` with changes coming from the outside state
        // e.g. the state is updated from another component, etc.
        setCurrentValue((currentValue) => {
            if (!value || Number(value) === Number(currentValue)) {
                return currentValue;
            }

            return Number(value).toFixed(2);
        });
    }, [value]);

    const { currencySymbol, isSymbolOnRight, currencySymbolWidth } = useMemo(() => {
        const formatter = Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode
        });

        const parts = formatter.formatToParts(100);

        const currencySymbol = parts.find((part) => part.type === 'currency')?.value;
        const isSymbolOnRight = parts[parts.length - 1].type === 'currency';

        const currencySymbolWidth = `${32 + ((currencySymbol?.length ?? 0) - 1) * 12}px`;

        return {
            currencySymbol,
            isSymbolOnRight,
            currencySymbolWidth
        };
    }, [currencyCode, locale]);

    const { inputPaddingLeft, inputPaddingRight, borderRadius } = useMemo(() => {
        if (!useInternalSymbol) {
            return {
                inputPaddingLeft: undefined, // use the default padding
                inputPaddingRight: undefined, // use the default padding
                borderRadius: isSymbolOnRight ? '4px 0px 0px 4px' : '0px 4px 4px 0px'
            };
        }

        return {
            inputPaddingLeft: isSymbolOnRight ? '16px' : currencySymbolWidth,
            inputPaddingRight: isSymbolOnRight ? currencySymbolWidth : '16px',
            borderRadius: undefined // use the default border radius
        };
    }, [useInternalSymbol, isSymbolOnRight, currencySymbolWidth]);

    return (
        <InputGroup
            variant={useInternalSymbol ? 'outline' : 'withAddon'}
            sx={{
                input: {
                    borderRadius,
                    height
                }
            }}
        >
            {!isSymbolOnRight &&
                (useInternalSymbol ? (
                    <InputLeftElement width={currencySymbolWidth}>
                        {currencySymbol}
                    </InputLeftElement>
                ) : (
                    <InputLeftAddon>{currencySymbol}</InputLeftAddon>
                ))}
            <Input
                min={0}
                max={100000}
                placeholder="0.00"
                step={0.01}
                type="number"
                paddingLeft={inputPaddingLeft}
                paddingRight={inputPaddingRight}
                {...props}
                value={currentValue}
                onChange={handleInputChange}
                onBlur={handleOnBlur}
            />
            {isSymbolOnRight &&
                (useInternalSymbol ? (
                    <InputRightElement width={currencySymbolWidth}>
                        {currencySymbol}
                    </InputRightElement>
                ) : (
                    <InputRightAddon>{currencySymbol}</InputRightAddon>
                ))}
        </InputGroup>
    );
};

export default CurrencyInput;
